import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import type { UserDisplaySettings } from '@/models/user';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Copy, Save } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';

type ActiveWorkspace = UserDisplaySettings['activeWorkspace'];

interface OpenLinkSettingsCardProps {
  workspaceId: string;
  activeWorkspace: ActiveWorkspace;
  onUpdate: () => Promise<void>;
}

const normalizeSlug = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);

export const OpenLinkSettingsCard: React.FC<OpenLinkSettingsCardProps> = ({
  workspaceId,
  activeWorkspace,
  onUpdate,
}) => {
  const [slug, setSlug] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSlug(activeWorkspace.slug || '');
    setMinAmount(activeWorkspace.openLinkMinAmountUsd != null ? String(activeWorkspace.openLinkMinAmountUsd) : '');
    setMaxAmount(activeWorkspace.openLinkMaxAmountUsd != null ? String(activeWorkspace.openLinkMaxAmountUsd) : '');
  }, [activeWorkspace.slug, activeWorkspace.openLinkMinAmountUsd, activeWorkspace.openLinkMaxAmountUsd]);

  const publicUrl = useMemo(() => {
    if (!slug.trim()) return '';
    return `${window.location.origin}/open/${encodeURIComponent(slug.trim())}`;
  }, [slug]);

  const copy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      sonnerToast.success(message);
    } catch {
      sonnerToast.error('Could not copy automatically.');
    }
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedSlug = normalizeSlug(slug || activeWorkspace.slug || '');
    if (!normalizedSlug || normalizedSlug.length < 3) {
      sonnerToast.error('Slug must be at least 3 characters.');
      return;
    }

    const parsedMin = minAmount.trim() ? Number(minAmount) : null;
    const parsedMax = maxAmount.trim() ? Number(maxAmount) : null;
    if (parsedMin != null && (!Number.isFinite(parsedMin) || parsedMin < 50 || parsedMin > 50000)) {
      sonnerToast.error('Min amount must be between $50 and $50,000.');
      return;
    }
    if (parsedMax != null && (!Number.isFinite(parsedMax) || parsedMax < 50 || parsedMax > 50000)) {
      sonnerToast.error('Max amount must be between $50 and $50,000.');
      return;
    }
    if (parsedMin != null && parsedMax != null && parsedMin > parsedMax) {
      sonnerToast.error('Min amount cannot exceed max amount.');
      return;
    }

    setIsSaving(true);
    try {
      const updateFn = httpsCallable(firebaseFunctions, 'updateWorkspaceSettings');
      await updateFn({
        workspaceId,
        slug: normalizedSlug,
        openLinkMinAmountUsd: parsedMin,
        openLinkMaxAmountUsd: parsedMax,
      });
      sonnerToast.success('Open payment link settings saved.');
      await onUpdate();
    } catch (error: any) {
      sonnerToast.error(`Failed to save open link settings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Open Payment Link</CardTitle>
        <CardDescription className="text-slate-600">
          A static link you can put in your bio, website, or front desk sign so buyers can enter their own amount and check out.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="open-link-slug" className="text-sm font-medium text-slate-900">Link slug</label>
            <div className="flex gap-2">
              <Input
                id="open-link-slug"
                value={slug}
                onChange={(e) => setSlug(normalizeSlug(e.target.value))}
                placeholder="my-studio"
                disabled={isSaving}
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
              />
              <Button type="button" variant="outline" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" onClick={() => publicUrl && copy(publicUrl, 'Open link copied.')} disabled={!publicUrl}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
              </Button>
            </div>
            {publicUrl && (
              <p className="text-xs text-muted-foreground">
                {publicUrl}
              </p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="open-link-min" className="text-sm font-medium text-slate-900">Min amount ($)</label>
              <Input
                id="open-link-min"
                inputMode="decimal"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="50"
                disabled={isSaving}
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="open-link-max" className="text-sm font-medium text-slate-900">Max amount ($)</label>
              <Input
                id="open-link-max"
                inputMode="decimal"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="50000"
                disabled={isSaving}
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
              />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Defaults are $50 minimum and $50,000 maximum if left blank.
          </p>

          <div className="flex justify-end">
            <Button type="submit" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              Save Open Link
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
